/**
 * Tracks an active CSV import so a floating progress pill can survive page
 * navigation. Covers two phases:
 *
 * 1. **Store phase** — companies/deals/pending rows written server-side via
 *    `run-csv-import-job`. Safe to close the import dialog; the pill polls
 *    `csv_import_jobs` until terminal.
 * 2. **Scoring / enrichment phase** — post-import contact onboarding (ICP
 *    scoring + first enrichment burst). Polls contact rows.
 *
 * Scope: ONE active job per user session. If another CSV import lands while
 * one is in flight, the newer job replaces the older — the older one keeps
 * running server-side but the pill follows the freshest import.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'nynch-csv-import-progress-v2';

export type CsvImportEntityType =
  | 'contacts'
  | 'companies'
  | 'deals'
  | 'pending_contacts';

export interface ActiveCsvImport {
  /** Server-side store job (csv_import_jobs.id). Present for background store imports. */
  storeJobId?: string;
  /** Workspace the store job belongs to (pill lives outside WorkspaceProvider). */
  workspaceId?: string;
  entityType?: CsvImportEntityType;
  /** Total rows in the store job (for progress denominator). */
  totalRows?: number;
  /** Optional file label for the pill. */
  label?: string;

  /** All contact IDs the post-import automation just kicked off scoring on. */
  contactIds: string[];
  /** Imported contacts actually selected for the first enrichment burst. */
  enrichContactIds?: string[];
  /** True once the scoring edge function finished, including row-level failures. */
  scoringComplete?: boolean;
  /** Number of contacts the scoring edge function could not score. */
  scoringFailedCount?: number;
  /** True once the first enrichment burst finished, including failures. */
  enrichmentComplete?: boolean;
  /** ICPs the scoring is running against (display names). Capped to 2. */
  icpNames: string[];
  /** Wall-clock ISO timestamp the job started. */
  startedAt: string;
}

interface CsvImportProgressContextValue {
  activeImport: ActiveCsvImport | null;
  setActiveImport: (job: ActiveCsvImport | null) => void;
}

const noopCtx: CsvImportProgressContextValue = {
  activeImport: null,
  setActiveImport: () => {},
};

const CsvImportProgressContext = createContext<CsvImportProgressContextValue>(noopCtx);

function isValidActiveImport(value: unknown): value is ActiveCsvImport {
  if (!value || typeof value !== 'object') return false;
  const v = value as ActiveCsvImport;
  if (typeof v.startedAt !== 'string') return false;
  const hasStore = typeof v.storeJobId === 'string' && v.storeJobId.length > 0;
  const hasContacts = Array.isArray(v.contactIds) && v.contactIds.length > 0;
  return hasStore || hasContacts;
}

function loadFromSession(): ActiveCsvImport | null {
  if (typeof window === 'undefined') return null;
  try {
    // Prefer v2; fall back to v1 scoring-only shape.
    const raw =
      window.sessionStorage.getItem(STORAGE_KEY) ??
      window.sessionStorage.getItem('nynch-csv-import-progress-v1');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveCsvImport;
    if (!isValidActiveImport(parsed)) return null;
    return {
      ...parsed,
      contactIds: Array.isArray(parsed.contactIds) ? parsed.contactIds : [],
      icpNames: Array.isArray(parsed.icpNames) ? parsed.icpNames : [],
    };
  } catch {
    return null;
  }
}

export function CsvImportProgressProvider({ children }: { children: ReactNode }) {
  const [activeImport, setActiveImportState] = useState<ActiveCsvImport | null>(() => loadFromSession());

  const setActiveImport = useCallback((job: ActiveCsvImport | null) => {
    setActiveImportState(job);
    if (typeof window === 'undefined') return;
    try {
      if (job) {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(job));
        // Keep v1 key cleared so we don't resurrect a stale scoring-only job.
        window.sessionStorage.removeItem('nynch-csv-import-progress-v1');
      } else {
        window.sessionStorage.removeItem(STORAGE_KEY);
        window.sessionStorage.removeItem('nynch-csv-import-progress-v1');
      }
    } catch {
      // sessionStorage can throw in some privacy modes — non-fatal
    }
  }, []);

  // If the job is older than 4 hours, auto-discard on mount. Store imports and
  // scoring should finish well under that; anything past 4 hours is almost
  // certainly a stale entry from a crashed session.
  useEffect(() => {
    if (!activeImport) return;
    const startedAt = Date.parse(activeImport.startedAt);
    if (!Number.isFinite(startedAt)) return;
    const ageHours = (Date.now() - startedAt) / (1000 * 60 * 60);
    if (ageHours > 4) setActiveImport(null);
  }, [activeImport, setActiveImport]);

  const value = useMemo(() => ({ activeImport, setActiveImport }), [activeImport, setActiveImport]);

  return (
    <CsvImportProgressContext.Provider value={value}>
      {children}
    </CsvImportProgressContext.Provider>
  );
}

export function useCsvImportProgress() {
  return useContext(CsvImportProgressContext);
}
